/**
 * ============================================================================
 * GUIDED TOUR FRAMEWORK - TYPE DEFINITIONS
 * ============================================================================
 *
 * This file contains all TypeScript interfaces and types for the guided tour
 * framework. The framework supports:
 * - Element highlighting with backdrop cutouts
 * - Tooltip popovers with auto-positioning
 * - Explanation cards with animated connector lines
 *
 * @module tour
 * @version 1.0.0
 * @created 2026-01-07
 */

// =============================================================================
// HIGHLIGHT TYPES
// =============================================================================

/**
 * Represents a single element highlight in the tour.
 * Highlights create a "cutout" in the backdrop overlay to draw attention
 * to specific UI elements.
 */
export interface TourHighlight {
    /** Unique identifier for this highlight */
    id: string;

    /**
     * CSS selector for the target element.
     * Can be any valid querySelector string (e.g., '#my-button', '.my-class', '[data-tour="step-1"]')
     */
    selector: string;

    /**
     * Additional padding around the element in pixels.
     * Adds extra space around the highlighted element.
     * @default 4
     */
    padding?: number;

    /**
     * Border radius for the highlight cutout in pixels.
     * If not specified, will attempt to match the element's computed border-radius.
     * @default 'auto'
     */
    borderRadius?: number | 'auto';

    /**
     * Glow color for the highlight border.
     * Accepts any valid CSS color value.
     * @default 'var(--primary-color)'
     */
    glowColor?: string;

    /**
     * Animation duration in milliseconds for highlight transitions.
     * @default 300
     */
    animationDuration?: number;
}

/**
 * Computed position data for a highlight, recalculated on scroll/resize.
 * This is the runtime representation after resolving the selector.
 */
export interface HighlightRect {
    /** Highlight ID from TourHighlight */
    id: string;

    /** Left position relative to viewport */
    x: number;

    /** Top position relative to viewport */
    y: number;

    /** Width of the highlight area */
    width: number;

    /** Height of the highlight area */
    height: number;

    /** Border radius to apply */
    borderRadius: number;

    /** Glow color for the border */
    glowColor: string;
}

// =============================================================================
// TOOLTIP TYPES
// =============================================================================

/**
 * Position preference for tooltip placement.
 * The tooltip will attempt to use the preferred position but will
 * automatically flip if there isn't enough viewport space.
 */
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/**
 * Configuration for a tour tooltip popover.
 */
export interface TourTooltip {
    /** Unique identifier for this tooltip */
    id: string;

    /**
     * CSS selector for the target element to attach the tooltip to.
     * Typically the same as a highlight selector.
     */
    targetSelector: string;

    /** Main title text for the tooltip */
    title?: string;

    /** Description/body text for the tooltip */
    description?: string;

    /**
     * Preferred position for the tooltip relative to the target.
     * @default 'auto'
     */
    position?: TooltipPosition;

    /** Show navigation buttons (Next/Previous) */
    showNavigation?: boolean;

    /** Custom "Next" button text */
    nextButtonText?: string;

    /** Custom "Previous" button text */
    prevButtonText?: string;

    /** Show a "Skip Tour" link */
    showSkip?: boolean;

    /** Custom panel CSS class for styling */
    panelClass?: string;

    /**
     * Offset distance from the target element in pixels.
     * @default 12
     */
    offset?: number;
}

// =============================================================================
// EXPLANATION CARD TYPES
// =============================================================================

/**
 * A single step item within an explanation card.
 * Each step can optionally connect to a highlighted element via a dashed line.
 */
export interface CardStep {
    /** Step number (1, 2, 3...) */
    stepNumber: number;

    /** Text description for this step */
    text: string;

    /**
     * Optional CSS selector for the element this step points to.
     * If provided, a dashed connector line will be drawn from the step
     * icon to the target element.
     */
    targetSelector?: string;

    /**
     * Color for the connector line.
     * @default 'var(--primary-color)'
     */
    lineColor?: string;
}

/**
 * Configuration for an explanation card with numbered steps
 * and optional connector lines to highlighted elements.
 */
export interface TourExplanationCard {
    /** Unique identifier for this card */
    id: string;

    /** Card title */
    title?: string;

    /** Array of step items to display */
    steps: CardStep[];

    /**
     * Preferred position for the card.
     * The card will automatically position to avoid overlapping highlights.
     * @default 'auto'
     */
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' | 'auto';

    /** Show a close button on the card */
    showClose?: boolean;

    /** Show a "Got it" or confirmation button */
    showConfirm?: boolean;

    /** Custom confirm button text */
    confirmButtonText?: string;

    /**
     * Animation duration for connector line drawing in milliseconds.
     * @default 500
     */
    lineAnimationDuration?: number;

    /** Whether to animate the connector lines */
    animateLines?: boolean;
}

// =============================================================================
// TOUR STEP & CONFIGURATION
// =============================================================================

/**
 * A single step in a guided tour sequence.
 * Combines highlighting, tooltip, and card configurations.
 */
export interface TourStep {
    /** Unique identifier for this step */
    id: string;

    /** Step name for debugging/tracking */
    name?: string;

    /**
     * Route to navigate to when this step becomes active.
     * Navigation happens BEFORE highlights are computed.
     * Use Angular router URL format (e.g., '/app/create-farm').
     */
    navigateTo?: string;

    /**
     * Delay in milliseconds after navigation before showing highlights.
     * Allows the page to fully render before highlighting elements.
     * @default 300
     */
    navigationDelay?: number;

    /**
     * Optional selector to wait for before showing highlights.
     * Useful when navigating to a new page to ensure the target element exists.
     * Overrides navigationDelay if present.
     */
    waitForSelector?: string;

    /**
     * Elements to highlight during this step.
     * Can highlight multiple elements simultaneously.
     */
    highlights?: TourHighlight[];

    /** Tooltip configuration for this step (optional) */
    tooltip?: TourTooltip;

    /** Explanation card configuration for this step (optional) */
    explanationCard?: TourExplanationCard;

    /**
     * Callback function when this step becomes active.
     */
    onEnter?: () => void;

    /**
     * Callback function when leaving this step.
     */
    onLeave?: () => void;

    /**
     * If true, clicking the backdrop will advance to the next step.
     * @default false
     */
    advanceOnBackdropClick?: boolean;

    /**
     * If true, clicking the highlighted element will advance to the next step.
     * @default false
     */
    advanceOnElementClick?: boolean;

    /**
     * Delay in milliseconds before auto-advancing to next step.
     * If not set, step waits for user interaction.
     */
    autoAdvanceDelay?: number;
}

/**
 * Configuration for an entire guided tour.
 */
export interface TourConfig {
    /** Unique identifier for this tour */
    id: string;

    /** Display name for the tour */
    name?: string;

    /**
     * Optional subtitle key for the contextual prompt.
     * Used when the tour is suggested in the bottom-right widget.
     */
    contextualSubtitle?: string;

    /** Array of steps in order */
    steps: TourStep[];

    /**
     * Backdrop opacity (0-1).
     * @default 0.6
     */
    backdropOpacity?: number;

    /**
     * Backdrop color.
     * @default 'rgba(0, 0, 0, 0.6)'
     */
    backdropColor?: string;

    /**
     * Enable keyboard navigation (arrow keys, escape).
     * @default true
     */
    keyboardNavigation?: boolean;

    /**
     * Close tour when clicking outside highlighted elements.
     * @default false
     */
    closeOnOutsideClick?: boolean;

    /**
     * Callback when tour starts.
     */
    onStart?: () => void;

    /**
     * Callback when tour completes (all steps finished).
     */
    onComplete?: () => void;

    /**
     * Callback when tour is skipped/closed early.
     */
    onSkip?: () => void;

    /**
     * Callback for each step change.
     */
    onStepChange?: (currentStep: number, previousStep: number) => void;
}

// =============================================================================
// TOUR STATE
// =============================================================================

/**
 * Runtime state of the tour service.
 * Used internally by TourService/TourStore.
 */
export interface TourState {
    /** Whether a tour is currently active */
    isActive: boolean;

    /** The current tour configuration (null if no tour loaded) */
    currentTour: TourConfig | null;

    /** Index of the current step (0-based) */
    currentStepIndex: number;

    /** Computed highlight rectangles for the current step */
    activeHighlights: HighlightRect[];

    /** Active tooltip configuration */
    activeTooltip: TourTooltip | null;

    /** Active explanation card configuration */
    activeCard: TourExplanationCard | null;

    /** Whether the tour is paused (e.g., during transitions) */
    isPaused: boolean;
}

/**
 * Initial state for the tour store.
 */
export const initialTourState: TourState = {
    isActive: false,
    currentTour: null,
    currentStepIndex: 0,
    activeHighlights: [],
    activeTooltip: null,
    activeCard: null,
    isPaused: false,
};
